import {IconButton} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowRightLine, ChevronDownLine, ChevronUpLine} from '@dropbox/dig-icons/assets';
import type {
  RedactedWorkstreamMetric,
  WorkstreamEmployee,
  WorkstreamMetric,
  WorkstreamUpdate,
} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {StatusButton} from 'components/DSYS/StatusButton';
import {isClosedStatus} from 'components/DSYS/StatusButtonIcon';
import {useExclusiveHover} from 'components/DSYS/useExclusiveHover';
import {useHoverShade} from 'components/DSYS/useHoverShade';
import {differenceInCalendarDays, format} from 'date-fns';
import {formatDayAge} from 'helpers/dates';
import {
  type ComponentProps,
  type CSSProperties,
  Fragment,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {getWorkstreamDescriptionPlainText} from 'views/workstreams/detail/workstreamDescriptionRichText';
import {prefetchWorkstream, useWorkstream} from 'views/workstreams/hooks';
import {getWorkstreamUpdateVisualStatus} from 'views/workstreams/utils';

import {FadeIn} from './FadeIn';
import {FoldToggle} from './FoldToggle';
import {InlineToggletip} from './InlineToggletip';
import {isSnapshotRow} from './landingSnapshot';
import {NorthStarByline, useNorthStarItem} from './NorthStarByline';
import {PersonLink} from './PersonLink';
import {ScanPlaceOrigin, ScanSectionOrigin, useScanCardOrigin} from './scanCardOrigin';
import {ScanProgressList} from './ScanProgressList';
import {SectionFoldToggle} from './SectionFoldToggle';
import {SectionLabel} from './SectionLabel';
import {RemoveSubworkstreamButton} from './SubworkstreamEdits';
import {UpdatePost} from './UpdateCard';
import {SummaryLine, SummaryPending} from './UpdateSummaryQuote';
import {type UpdateSummaryState, useUpdateSummary} from './useUpdateSummary';
import {
  logWorkstreamsInteraction,
  opensInNewTab,
  type UpdateOpenedFrom,
  type WorkstreamsViewPlace,
} from './workstreamsAnalytics';
import {workstreamsPath} from './workstreamsRoutes';

/** The fields the list reads; satisfied by list rows and by sub-workstreams. */
export type ScanWorkstream = {
  id: number;
  name: string;
  updates?: WorkstreamUpdate[];
  employee_associations: WorkstreamEmployee[];
  status_update_needed?: boolean | number;
  status_due_date?: string | null;
  dependencies_count?: number;
  metrics: Array<WorkstreamMetric | RedactedWorkstreamMetric>;
  archived?: boolean | null;
};

// Fixed status column so every title / meta / excerpt starts at the same x
// across cards: fits "● Not started ›" with its button chrome (hovered / open)
// and still leaves room before the title.
export const STATUS_COLUMN_PX = 124;
export const COLUMN_GAP_PX = 12;
const CARD_INSET_PX = 16;
// StatusButton's 1px border + 8px horizontal padding, before its icon.
const STATUS_BUTTON_INSET_PX = 9;
// Readable line length for update text, so it doesn't run the full card width
// on wide screens.
const UPDATE_MAX_WIDTH_PX = 720;
// Space between the top-level list's boxes (an open card stands in its own).
const DETACHED_GAP = '12';

const latestUpdate = (workstream: ScanWorkstream) => workstream.updates?.[0];

export const isClosed = (workstream: ScanWorkstream) =>
  isClosedStatus(latestUpdate(workstream)?.status ?? '');

// Archived via the legacy flag or a latest "archived" health update.
export const isArchived = (workstream: ScanWorkstream) =>
  Boolean(workstream.archived) || latestUpdate(workstream)?.status === 'archived';

// A sub-workstream the viewer can't read comes back as a "Restricted"
// placeholder with no updates, so a status for it would be made up.
export const isRestricted = (workstream: ScanWorkstream) =>
  workstream.name === 'Restricted' && !workstream.updates?.length;

const daysSinceUpdate = (workstream: ScanWorkstream) => {
  const update = latestUpdate(workstream);
  return update ? differenceInCalendarDays(new Date(), new Date(update.created_at)) : 0;
};

// "Today", "Yesterday", "3d ago", "2w ago", "3mo ago". Days, not a weekday or
// date: on a Wednesday "Thu" could be tomorrow, and "Oct 23" needs today's date.
// Weeks start at 7 days, so an age in weeks means at least a cycle's gap.
const formatAge = (days: number) => {
  if (days <= 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${formatDayAge(days)} ago`;
};

// Esc collapses the most recently expanded row (innermost first when nested).
const expandedRowClosers: Array<() => void> = [];
let escListenerAttached = false;
const handleEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || event.defaultPrevented || expandedRowClosers.length === 0) return;
  const target = event.target as HTMLElement | null;
  if (target?.closest('input, textarea, [contenteditable="true"]')) return;
  // Leave Esc to any open dialog (e.g. the status update modal).
  if (document.querySelector('[role="dialog"]')) return;
  expandedRowClosers[expandedRowClosers.length - 1]();
  // Handled, so the detail page's Esc-to-close (on window) leaves the page open.
  event.preventDefault();
};
const useEscapeToCollapse = (expanded: boolean, collapse: () => void) => {
  useEffect(() => {
    if (!expanded) return;
    if (!escListenerAttached) {
      document.addEventListener('keydown', handleEscape);
      escListenerAttached = true;
    }
    expandedRowClosers.push(collapse);
    return () => {
      const index = expandedRowClosers.lastIndexOf(collapse);
      if (index >= 0) expandedRowClosers.splice(index, 1);
    };
  }, [expanded, collapse]);
};

export const ownerOf = (workstream: ScanWorkstream) =>
  workstream.employee_associations.find(({dri}) => dri === 'dri')?.employee ??
  workstream.employee_associations[0]?.employee;

const formatShortDate = (value?: string | null) =>
  value ? format(new Date(value), 'MMM d') : null;

// Titles, metric names, and update comments are stored as rich text.
export const plain = (value?: string | null) => getWorkstreamDescriptionPlainText(value).trim();

// "DRI · N sub-workstreams · ● X of Y milestones open". Milestone dots only
// for flagged (at risk / off track) ones, so a colored dot means "look here".
// Line 2: the DRI (the Activity log records who posted the update), then the
// update itself with a hanging indent — AI TL;DR when collapsed, the full
// update when expanded.
// Line 2 (collapsed only): the DRI · sub-workstreams · the starred north star's
// value.
// Line 3 (collapsed only): the AI TL;DR, or the excerpt until it lands — in the
// same spot the full update takes when the card is expanded.
// What a closing update did, so the byline's when reads "Completed Aug 10"; an
// open workstream's is just the when ("2w ago"), like an open card's byline.
const UPDATE_VERBS: Record<string, string> = {
  complete: 'Completed',
  cancelled: 'Cancelled',
  missed: 'Missed',
  archived: 'Archived',
  closed: 'Closed',
};
export const withUpdateVerb = (status: string | null | undefined, when: string) => {
  const verb = UPDATE_VERBS[status ?? ''];
  return verb ? `${verb} ${when}` : when;
};

// On a closed card a plain click on the title opens the card, like the rest of
// the header; it stays a link to the full page so cmd-click, middle-click and
// right-click can open it in a new tab. An expanded card lets a plain click on
// the title follow it to the full page, and shows a More details link after it;
// each underlines only while it's hovered itself. A new-tab open of either is
// logged here, since the page's view events only see it as a load in the new tab.
export const TitleLink = ({
  workstream,
  expanded,
  cardHovered,
}: {
  workstream: ScanWorkstream;
  expanded: boolean;
  cardHovered: boolean;
}) => {
  const href = workstreamsPath(workstream.id, 'detail');
  const [linkHovered, setLinkHovered] = useState(false);
  const [titleHovered, setTitleHovered] = useState(false);
  // Truncate's portaled tooltip can skip a mouseleave; the card's exclusive
  // hover can't strand, so it gates the links'.
  useEffect(() => {
    if (!cardHovered) {
      setLinkHovered(false);
      setTitleHovered(false);
    }
  }, [cardHovered]);
  // The host handles plain clicks; a modifier click opens a tab, and shouldn't
  // also toggle the card.
  const stopToggle = (event: React.MouseEvent) => event.stopPropagation();
  // Middle-clicks only fire auxclick, so each link listens for both.
  const origin = useScanCardOrigin();
  const logNewTab = (link: 'title' | 'more_details') => (event: React.MouseEvent) => {
    if (opensInNewTab(event)) {
      logWorkstreamsInteraction('detail_opened_new_tab', {
        workstream_id: workstream.id,
        link,
        section: origin.section,
        from_place: origin.place,
      });
    }
  };

  return (
    <Box display="flex" alignItems="baseline" style={{gap: 8, minWidth: 0, flex: '0 1 auto'}}>
      <a
        href={href}
        // A closed card's host leaves this link to the header's toggle; an
        // expanded card's opens the full page, like More details.
        data-toggles-card={expanded ? undefined : true}
        // The header is the keyboard target; the link is a pointer shortcut.
        tabIndex={-1}
        onAuxClick={logNewTab('title')}
        onMouseEnter={() => setTitleHovered(true)}
        onMouseLeave={() => setTitleHovered(false)}
        onClick={(event: React.MouseEvent) => {
          if (expanded || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
            stopToggle(event);
            logNewTab('title')(event);
          } else {
            // Falls through to the header, which toggles the card.
            event.preventDefault();
          }
        }}
        className={atoms({color: 'inherit'})}
        style={{
          display: 'block',
          minWidth: 0,
          textDecoration: expanded && titleHovered ? 'underline' : 'none',
        }}
      >
        <Text isBold>
          <Truncate lines={2}>{workstream.name}</Truncate>
        </Text>
      </a>
      {expanded && (
        // Faint until hovered itself; the Text wraps the anchor so its label
        // and arrow take the same color.
        <Text
          size="small"
          isBold
          color={linkHovered ? 'standard' : 'faint'}
          style={{flexShrink: 0, whiteSpace: 'nowrap'}}
        >
          <a
            href={href}
            onAuxClick={logNewTab('more_details')}
            onClick={(event: React.MouseEvent) => {
              stopToggle(event);
              logNewTab('more_details')(event);
            }}
            onMouseEnter={() => setLinkHovered(true)}
            onMouseLeave={() => setLinkHovered(false)}
            className={atoms({color: 'inherit'})}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              textDecoration: linkHovered ? 'underline' : 'none',
            }}
          >
            More details
            <UIIcon src={ArrowRightLine} size="small" />
          </a>
        </Text>
      )}
    </Box>
  );
};

// Collapsed cards only: an open card's body (properties row, update byline,
// sub-workstreams) already shows all of it.
const PostLine = ({
  workstream,
  summary,
  summaryEdited,
  excerpt,
}: {
  workstream: ScanWorkstream;
  summary?: string;
  summaryEdited?: boolean;
  excerpt: string;
}) => {
  const update = latestUpdate(workstream);
  const person = ownerOf(workstream) ?? update?.author;
  const showUpdate = Boolean(update) && Boolean(summary || excerpt);
  const freshness = getFreshness(workstream);
  // The north star's value ends the byline while the workstream is open.
  const northStarItem = useNorthStarItem(workstream);
  const northStar =
    northStarItem?.value !== undefined && !isClosed(workstream) && !isArchived(workstream)
      ? northStarItem
      : undefined;

  return (
    <Box display="flex" flexDirection="column" style={{gap: 2, minWidth: 0}}>
      {(person || freshness || northStar) && (
        <Box
          display="flex"
          alignItems="center"
          style={{gap: 6, minWidth: 0, minHeight: 20, maxWidth: UPDATE_MAX_WIDTH_PX}}
        >
          {person && <PersonLink person={person} />}
          {/* "Name · 2w ago" ("Completed Aug 10"). */}
          {freshness && (
            <Text size="small" color="faint" style={{whiteSpace: 'nowrap', flexShrink: 0}}>
              {person ? '· ' : ''}
              {withUpdateVerb(update?.status, freshness.label)}
            </Text>
          )}
          {northStar && (
            <>
              {(person || freshness) && (
                <Text size="small" color="faint">
                  ·
                </Text>
              )}
              <NorthStarByline item={northStar} />
            </>
          )}
        </Box>
      )}
      {showUpdate && (
        <Box style={{minWidth: 0, maxWidth: UPDATE_MAX_WIDTH_PX}}>
          {summary ? (
            <SummaryLine summary={summary} lines={2} edited={summaryEdited} />
          ) : (
            <Text size="small">
              <Truncate lines={2}>{excerpt}</Truncate>
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};

// When the latest update landed, the same everywhere a card says it (after the
// name in a closed card's byline and in an open card's update byline): no
// "Updated" / "Closed" (the status says which); the age ("3d ago", "6w ago"),
// or the date when closed. An overdue update shows in My's Update due section,
// not here.
export const getFreshness = (workstream: ScanWorkstream): {label: string} | undefined => {
  const update = latestUpdate(workstream);
  // Nothing before the first update: the status reads "Not started" and the
  // card has no excerpt, so it would only repeat that.
  if (!update) return undefined;
  const label = isClosed(workstream)
    ? (formatShortDate(update.created_at) ?? '')
    : formatAge(daysSinceUpdate(workstream));
  return {label};
};

// Hairline between cards, inset to the card padding (not edge to edge).
export const InsetDivider = () => (
  <Box
    backgroundColor="Border Subtle"
    style={{height: 1, marginLeft: CARD_INSET_PX, marginRight: CARD_INSET_PX}}
  />
);

// The fold row's chevron and label line up with the cards' status icon and label
// (inside the status button's 1px border + 8px padding, 6px icon gap).
const FOLD_ROW_PADDING = `12px ${CARD_INSET_PX}px 12px ${CARD_INSET_PX + STATUS_BUTTON_INSET_PX}px`;

// Body of an expanded card, below the (clickable) header: the latest update with
// the detail page's byline (clamped, at a readable width; no TL;DR, since the real
// thing is here), then the Progress list (north star, then metrics & milestones
// folded), then the (folded) sub-workstreams box — all in the title column and outside the click target, so text stays
// selectable.
const ExpandedDetails = ({
  workstream,
  depth,
  inset,
  insetLeft,
  summary,
  subsInitiallyOpen,
  hideSubworkstreams,
  onLeaveUpdate,
}: {
  workstream: ScanWorkstream;
  depth: number;
  inset: number;
  // The left padding: `inset` plus an open card's breakout growth.
  insetLeft: string;
  // The latest update's summary, if it has one (short updates don't).
  summary: UpdateSummaryState;
  subsInitiallyOpen: boolean;
  hideSubworkstreams: boolean;
  onLeaveUpdate: (id: number) => void;
}) => {
  const update = latestUpdate(workstream);
  const hasSubs = !hideSubworkstreams && (workstream.dependencies_count ?? 0) > 0;
  // Sub-workstreams start folded (their cards, and each one's summary, only
  // load once opened) unless the card opened on its own.
  const [subsOpen, setSubsOpen] = useState(subsInitiallyOpen);
  // Opened by a click (not open from the start), so they fade in.
  const [subsToggled, setSubsToggled] = useState(false);
  const {workstream: full, isLoading} = useWorkstream(
    hasSubs && subsOpen ? workstream.id : undefined
  );
  const subs = useMemo(
    () =>
      // Saved order, as the API returns them.
      (full?.dependencies ?? []).map(({dependent}) => dependent),
    [full?.dependencies]
  );
  const activeSubs = subs.filter((sub) => !isArchived(sub));
  const archivedSubs = subs.filter(isArchived);
  const titleColumnInset = `calc(${insetLeft} + ${STATUS_COLUMN_PX + COLUMN_GAP_PX}px)`;
  // Everything in the body, boxes included, sits on the title column.
  const bodyStyle: CSSProperties = {
    paddingLeft: titleColumnInset,
    paddingRight: inset,
  };

  return (
    <>
      <Box
        // The same 16px below the tinted header as between the boxes.
        paddingTop="16"
        paddingBottom="16"
        style={bodyStyle}
      >
        <Stack gap="16">
          {/* The detail page's byline + update, unboxed so it starts on the
              title column; the text keeps a readable width. The AI summary
              leads the post in the collapsed card's quiet style (no pull
              quote), and no status chip: the header's button shows it. Read-only:
              the update and its summary edit on the detail page. */}
          {update && (
            <Box style={{maxWidth: UPDATE_MAX_WIDTH_PX}}>
              <UpdatePost
                update={update}
                dri={ownerOf(workstream)}
                clamped
                lead={
                  summary.pending ? (
                    <SummaryPending size="small" />
                  ) : summary.summary ? (
                    <SummaryLine summary={summary.summary} edited={summary.edited} />
                  ) : undefined
                }
                when={getFreshness(workstream)}
              />
            </Box>
          )}
          {/* A parent's card (its subs listed below it) keeps them folded behind
              its north star, like any card, so the subs stay near the top. */}
          <ScanProgressList
            workstream={workstream}
            openFold={subsInitiallyOpen && !hideSubworkstreams}
          />
        </Stack>
      </Box>

      {/* In the title column, like the Progress list above. */}
      {hasSubs && (
        <Box paddingBottom="16" style={bodyStyle}>
          <SectionFoldToggle
            fold="subworkstreams"
            label="Sub-workstreams"
            open={subsOpen}
            onToggle={() => {
              setSubsToggled(true);
              setSubsOpen((value) => !value);
            }}
          />
          {subsOpen && (
            <FadeIn enabled={subsToggled}>
              {isLoading && subs.length === 0 ? (
                <Skeleton.Box height={48} />
              ) : (
                <SubworkstreamGroup
                  workstreams={activeSubs}
                  archived={archivedSubs}
                  depth={depth + 1}
                  onLeaveUpdate={onLeaveUpdate}
                />
              )}
            </FadeIn>
          )}
        </Box>
      )}
    </>
  );
};

export const ScanRow = ({
  workstream,
  depth,
  onLeaveUpdate,
  expanded: expandedProp,
  onExpandedChange,
  highlighted = false,
  headerAccessory,
  lone = false,
  hideSubworkstreams = false,
}: {
  workstream: ScanWorkstream;
  depth: number;
  onLeaveUpdate: (id: number, from?: UpdateOpenedFrom) => void;
  // Controlled by the top-level list, which lays open cards out on their own;
  // sub-workstream cards keep their own state.
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  // Lit as if its header were hovered (e.g. while its drag handle is).
  highlighted?: boolean;
  // Before the chevron while open (e.g. the focused workstream's facepile).
  headerAccessory?: ReactNode;
  // The only card in view: every open (header ones too) shows everything.
  lone?: boolean;
  // A parent's card above its sub-workstreams' cards: they're listed below it.
  hideSubworkstreams?: boolean;
}) => {
  const [ownExpanded, setOwnExpanded] = useState(false);
  const expanded = expandedProp ?? ownExpanded;
  // Its section and page, for its events (Esc's collapse reads the latest).
  const origin = useScanCardOrigin();
  const originRef = useRef(origin);
  originRef.current = origin;
  const setExpanded = onExpandedChange ?? setOwnExpanded;
  // Esc's collapse must stay one stable function (it's the closer stack's key).
  const setExpandedRef = useRef(setExpanded);
  setExpandedRef.current = setExpanded;
  // Whether the current open came from the header. A card that opened on its
  // own (or a lone card, however it opened) opens its sub-workstreams too, so
  // there's more to see.
  const [openedByToggle, setOpenedByToggle] = useState(false);
  const toggle = () => {
    logWorkstreamsInteraction('card_toggled', {
      workstream_id: workstream.id,
      expanded: !expanded,
      depth,
      section: origin.section,
      from_place: origin.place,
    });
    setOpenedByToggle(!expanded);
    setExpanded(!expanded);
  };
  // The whole card (header + open body) hovers; the header's tint and status
  // chrome only while the pointer is on the header, the part that toggles.
  const {
    hovered: cardHovered,
    onMouseEnter: hoverCard,
    onMouseLeave: handleMouseLeave,
  } = useExclusiveHover();
  const [pointerInHeader, setPointerInHeader] = useState(false);
  const hovered = (cardHovered && pointerInHeader) || highlighted;
  const shade = useHoverShade(hovered);
  // mouseover, not mouseenter: coming back out of a nested sub-workstream card
  // into this one's body re-hovers this card. Only the innermost card under the
  // pointer takes it (not its ancestors, nor via a portaled tooltip), and not
  // from a fold toggle in its body, which hovers itself.
  const handleMouseOver = (event: React.MouseEvent<HTMLElement>) => {
    if (
      (event.target as HTMLElement).closest('[data-scan-card], [data-hovers-self]') ===
      event.currentTarget
    ) {
      hoverCard();
    }
  };
  const [collapse] = useState(() => () => {
    logWorkstreamsInteraction('card_toggled', {
      workstream_id: workstream.id,
      expanded: false,
      depth,
      method: 'escape',
      section: originRef.current.section,
      from_place: originRef.current.place,
    });
    setExpandedRef.current(false);
  });
  useEscapeToCollapse(expanded, collapse);
  const update = latestUpdate(workstream);
  const status = update?.status ?? 'no_status';
  const excerpt = plain(update?.comment);
  // AI one-liner of the latest update; the plain excerpt shows until it lands.
  // Short updates already read as a summary — show them as written, no AI.
  const summary = useUpdateSummary(update);
  // Every card (sub-workstreams too) spans the card width, so statuses align.
  const inset = CARD_INSET_PX;
  // An open top-level card's frame grows left on wide panes (Layout's breakout);
  // its rows take that back, so the status stays on the column.
  const insetLeft = `calc(${inset}px + var(--dsys-breakout-inset, 0px))`;

  return (
    <Box
      position="relative"
      data-scan-card
      onMouseOver={handleMouseOver}
      onMouseLeave={handleMouseLeave}
      style={{minWidth: 0}}
    >
      {/* The header toggles the card, title included while it's closed; an
          open card's title and More details open the full page (the host
          intercepts /workstreams detail links). */}
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={(event) => {
          event.stopPropagation();
          toggle();
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
        // Mouse clicks don't take focus, so the focus ring only shows for keyboard use.
        onMouseDown={(event) => event.preventDefault()}
        onMouseEnter={() => {
          setPointerInHeader(true);
          // The open body needs the full workstream; hovering starts the fetch
          // so it's usually there by the click.
          if (!expanded) prefetchWorkstream(workstream.id);
        }}
        onMouseLeave={() => setPointerInHeader(false)}
        className={`${atoms({
          display: 'flex',
          paddingY: '16',
          cursor: 'pointer',
          // An open card's header keeps a light tint, marking where it starts;
          // its body stays untinted. The hover shade rises over it.
          backgroundColor: expanded ? 'Opacity Surface' : undefined,
        })} ${shade.className}`}
        style={{
          ...shade.style,
          gap: COLUMN_GAP_PX,
          paddingLeft: insetLeft,
          paddingRight: inset,
          minWidth: 0,
          overflow: 'hidden',
          // Inset, so the group's rounded clip doesn't cut the focus ring off.
          outlineOffset: -2,
        }}
      >
        {/* Status reads as a plain label; hovering the card (or opening it)
            reveals it as the update button (border, chevron; fill on hover) —
            same padding, so no shift. */}
        {/* -4px centers the 32px status button on the title's first (24px) line.
            A restricted one keeps the empty column, so its title still lines up. */}
        <Box style={{width: STATUS_COLUMN_PX, flexShrink: 0, marginTop: -4}}>
          {!isRestricted(workstream) && (
            <StatusButton
              status={getWorkstreamUpdateVisualStatus(status)}
              size="small"
              includeArchived
              // An open card keeps the outline + chevron, like the detail page's.
              hoverChromeOnly={!hovered && !expanded}
              hoverBackgroundOnly={!hovered}
              hideIconEnd={!hovered && !expanded}
              onClick={(event: React.SyntheticEvent) => {
                event.preventDefault();
                event.stopPropagation();
                onLeaveUpdate(workstream.id, {
                  button: 'card_status',
                  section: origin.section,
                  from_place: origin.place,
                });
              }}
            />
          )}
        </Box>

        <Box display="flex" flexDirection="column" style={{flex: 1, minWidth: 0, gap: 2}}>
          <Box display="flex" alignItems="flex-start" style={{gap: 12, minWidth: 0}}>
            <TitleLink workstream={workstream} expanded={expanded} cardHovered={cardHovered} />
            {/* The accordion's chevron, alone in the corner (the date moved under
                the status, so it no longer reads as a dropdown): ⌄ while a
                closed card is hovered, ^ on an open one to collapse it. The
                header is still the keyboard target (Esc too). */}
            <Box
              display="flex"
              alignItems="center"
              style={{height: 24, gap: 8, flexShrink: 0, marginLeft: 'auto'}}
            >
              {/* On a detail page's own sub-workstreams only (it sets the context). */}
              {hovered && depth === 0 && (
                <RemoveSubworkstreamButton id={workstream.id} name={workstream.name} />
              )}
              {expanded && headerAccessory && (
                // Its own clicks (a facepile's modal) don't toggle the card.
                <Box display="flex" alignItems="center" onClick={(e) => e.stopPropagation()}>
                  {headerAccessory}
                </Box>
              )}
              {expanded ? (
                <IconButton
                  variant="transparent"
                  size="small"
                  aria-label="Collapse"
                  tabIndex={-1}
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    toggle();
                  }}
                >
                  <UIIcon
                    src={ChevronUpLine}
                    size="small"
                    className={atoms({color: 'Text Subtle'})}
                  />
                </IconButton>
              ) : (
                hovered && (
                  // The header itself toggles; this only says so. Sits where the
                  // open card's button does (its 4px padding), so nothing shifts.
                  <Box style={{padding: 4, lineHeight: 0}}>
                    <UIIcon
                      src={ChevronDownLine}
                      size="small"
                      className={atoms({color: 'Text Subtle'})}
                    />
                  </Box>
                )
              )}
            </Box>
          </Box>

          {!expanded && (
            <PostLine
              workstream={workstream}
              summary={summary.summary}
              summaryEdited={summary.edited}
              excerpt={excerpt}
            />
          )}
        </Box>
      </div>

      {expanded && (
        // Fades in when opened from the header; one that opened on its own
        // (lone, restored) is just there.
        <FadeIn enabled={openedByToggle}>
          {isSnapshotRow(workstream) ? (
            // Last visit's card (landingSnapshot) is only its header; the body
            // waits for the live row, a moment away.
            <Box paddingY="16" style={{paddingLeft: insetLeft, paddingRight: inset}}>
              <Skeleton.Box height={48} />
            </Box>
          ) : (
            <ExpandedDetails
              workstream={workstream}
              depth={depth}
              inset={inset}
              insetLeft={insetLeft}
              summary={summary}
              subsInitiallyOpen={lone || !openedByToggle}
              hideSubworkstreams={hideSubworkstreams}
              onLeaveUpdate={onLeaveUpdate}
            />
          )}
        </FadeIn>
      )}
    </Box>
  );
};

// A collapsed "› Archived" row, then (once opened) the archived cards.
// Inset dividers, like Milestones.
export const ArchivedFold = ({
  archived,
  depth,
  onLeaveUpdate,
}: {
  archived: ScanWorkstream[];
  depth: number;
  onLeaveUpdate: (id: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <FoldToggle
        label="Archived"
        count={archived.length}
        open={open}
        rowPadding={FOLD_ROW_PADDING}
        onToggle={() => {
          logWorkstreamsInteraction('fold_toggled', {
            fold: 'archived_subworkstreams',
            expanded: !open,
          });
          setOpen(!open);
        }}
      />
      {open && (
        <FadeIn>
          {/* The first archived card sits right under its toggle, so no divider. */}
          {archived.map((workstream, index) => (
            <Fragment key={workstream.id}>
              {index > 0 && <InsetDivider />}
              <ScanRow workstream={workstream} depth={depth} onLeaveUpdate={onLeaveUpdate} />
            </Fragment>
          ))}
        </FadeIn>
      )}
    </>
  );
};

// One box of an expanded card's sub-workstreams: the cards, then a collapsed
// "Archived" row inside the same box. Inset dividers, like Milestones. (The
// detail page's own are sortable: SortableSubworkstreamGroup.)
const SubworkstreamGroup = ({
  workstreams,
  archived,
  depth = 0,
  onLeaveUpdate,
}: {
  workstreams: ScanWorkstream[];
  archived: ScanWorkstream[];
  depth?: number;
  onLeaveUpdate: (id: number) => void;
}) => {
  return (
    <Box
      borderStyle="Solid"
      borderColor="Border Subtle"
      borderWidth="1"
      borderRadius="Medium"
      // Nested cards don't grow, so they don't take the parent's breakout inset.
      style={{overflow: 'hidden', '--dsys-breakout-inset': '0px'} as CSSProperties}
    >
      {workstreams.map((workstream, index) => (
        <Fragment key={workstream.id}>
          {index > 0 && <InsetDivider />}
          <ScanRow workstream={workstream} depth={depth} onLeaveUpdate={onLeaveUpdate} />
        </Fragment>
      ))}
      {archived.length > 0 && (
        <>
          {workstreams.length > 0 && <InsetDivider />}
          <ArchivedFold archived={archived} depth={depth} onLeaveUpdate={onLeaveUpdate} />
        </>
      )}
    </Box>
  );
};

// An open card detaches into its own box; each run of collapsed cards between
// open ones shares a box. Rows stay flat siblings, each drawing its piece of
// the box border, so toggling a card never remounts it (and it keeps focus).
// A lone card (a focused workstream, a one-workstream pillar) starts open, unless
// other sections above or below it (`onlyGroup` false) hold cards too.
const RowGroup = ({
  workstreams,
  onlyGroup = true,
  onLeaveUpdate,
  loneAccessory,
  hideSubworkstreams,
}: {
  workstreams: ScanWorkstream[];
  onlyGroup?: boolean;
  onLeaveUpdate: (id: number) => void;
  // In the lone card's header, while it's open.
  loneAccessory?: ReactNode;
  hideSubworkstreams?: boolean;
}) => {
  // Ids flipped from their default (open when lone, else collapsed), so a list
  // that narrows to one card opens it.
  const [flipped, setFlipped] = useState<ReadonlySet<number>>(() => new Set());
  const lone = onlyGroup && workstreams.length === 1;
  const isExpanded = (id: number) => lone !== flipped.has(id);
  const setExpanded = (id: number, open: boolean) =>
    setFlipped((previous) => {
      const next = new Set(previous);
      if (open === lone) next.delete(id);
      else next.add(id);
      return next;
    });

  const corner = (rounded: boolean) => (rounded ? 'Medium' : undefined);

  return (
    <Box>
      {workstreams.map((workstream, index) => {
        const expanded = isExpanded(workstream.id);
        const previous = workstreams[index - 1];
        const following = workstreams[index + 1];
        const startsBox = !previous || expanded || isExpanded(previous.id);
        const endsBox = expanded || !following || isExpanded(following.id);
        return (
          // Clipped, so a tinted first / last card follows the rounded corners.
          // Every row's top border is its box's top edge or the divider above it.
          <Box
            key={workstream.id}
            borderTop="Solid"
            borderLeft="Solid"
            borderRight="Solid"
            borderBottom={endsBox ? 'Solid' : undefined}
            borderColor="Border Subtle"
            borderTopLeftRadius={corner(startsBox)}
            borderTopRightRadius={corner(startsBox)}
            borderBottomLeftRadius={corner(endsBox)}
            borderBottomRightRadius={corner(endsBox)}
            marginTop={startsBox && previous ? DETACHED_GAP : undefined}
            // An open card in a list lifts off it; a lone one has nothing to
            // lift off.
            boxShadow={expanded && !lone ? 'Raised' : undefined}
            // An open card grows right to the pane's gutter, up to wide.
            className={expanded ? Layout.breakoutWideClassName : undefined}
            style={{overflow: 'hidden'}}
          >
            <ScanRow
              workstream={workstream}
              depth={0}
              onLeaveUpdate={onLeaveUpdate}
              expanded={expanded}
              onExpandedChange={(open) => setExpanded(workstream.id, open)}
              headerAccessory={lone ? loneAccessory : undefined}
              lone={lone}
              hideSubworkstreams={hideSubworkstreams}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// The list's last section: an "Archived" label like the role / pillar ones
// above it, folded shut, opening onto its cards in the same boxes.
const ArchivedSection = ({
  archived,
  onLeaveUpdate,
}: {
  archived: ScanWorkstream[];
  onLeaveUpdate: (id: number) => void;
}) => {
  const [open, setOpen] = useState(false);
  return (
    <Box>
      <SectionFoldToggle
        fold="archived_section"
        label="Archived"
        open={open}
        onToggle={() => setOpen(!open)}
      />
      {open && (
        <FadeIn>
          <RowGroup workstreams={archived} onlyGroup={false} onLeaveUpdate={onLeaveUpdate} />
        </FadeIn>
      )}
    </Box>
  );
};

// The loading list, in the shape the page will show (each caller knows it from
// the URL): `list` a run of cards (a pillar), `sections` a label over a run
// (My, a person, Company), `parent` a card over its Sub-workstreams, `card` one
// open card (a focused workstream).
export type ScanSkeletonShape = 'list' | 'sections' | 'parent' | 'card';

type SkeletonWidth = ComponentProps<typeof Skeleton.Text>['width'];
type SkeletonRow = {title: SkeletonWidth; owner: SkeletonWidth; excerpt?: SkeletonWidth};
const SKELETON_ROWS: SkeletonRow[] = [
  {title: 300, owner: 88, excerpt: 240},
  {title: 220, owner: 104},
  {title: 260, owner: 80, excerpt: 300},
  {title: 180, owner: 96},
  {title: 240, owner: 88, excerpt: 200},
];

const SkeletonFrame = ({children, open = false}: {children: ReactNode; open?: boolean}) => (
  <Box
    borderStyle="Solid"
    borderColor="Border Subtle"
    borderWidth="1"
    borderRadius="Medium"
    // An open card grows right like a loaded one.
    className={open ? Layout.breakoutWideClassName : undefined}
    style={{overflow: 'hidden'}}
  >
    {children}
  </Box>
);

// ScanRow's header: the status on the StatusButton's inset, then the title's
// 24px line; a closed card adds its "Owner · 2w ago" line and an excerpt.
const SkeletonCardRow = ({
  row,
  divider = false,
  open = false,
}: {
  row: SkeletonRow;
  divider?: boolean;
  open?: boolean;
}) => (
  <Box
    display="flex"
    paddingY="16"
    borderTop={divider ? 'Solid' : undefined}
    borderColor="Border Subtle"
    backgroundColor={open ? 'Opacity Surface' : undefined}
    style={{
      gap: COLUMN_GAP_PX,
      paddingLeft: `calc(${CARD_INSET_PX}px + var(--dsys-breakout-inset, 0px))`,
      paddingRight: CARD_INSET_PX,
    }}
  >
    <Box
      display="flex"
      alignItems="center"
      style={{
        width: STATUS_COLUMN_PX,
        flexShrink: 0,
        gap: 8,
        height: 24,
        paddingLeft: STATUS_BUTTON_INSET_PX,
      }}
    >
      <Skeleton.Box width={12} height={12} borderRadius="Circular" />
      <Skeleton.Text size="small" width={56} />
    </Box>
    <Box display="flex" flexDirection="column" style={{flex: 1, minWidth: 0, gap: 2}}>
      <Box display="flex" alignItems="center" style={{height: 24, gap: 12}}>
        <Skeleton.Text width={row.title} />
        {open && <Skeleton.Text size="small" width={88} />}
      </Box>
      {!open && (
        <>
          <Box display="flex" alignItems="center" style={{gap: 6, height: 20}}>
            <Skeleton.Avatar size="xsmall" />
            <Skeleton.Text size="small" width={row.owner} />
            <Skeleton.Text size="small" width={48} />
          </Box>
          {row.excerpt && (
            <Box display="flex" alignItems="center" style={{height: 20}}>
              <Skeleton.Text size="small" width={row.excerpt} />
            </Box>
          )}
        </>
      )}
    </Box>
  </Box>
);

// With `fade`, each card dims a step more than the one above, like the
// table's skeleton rows.
const SkeletonCards = ({rows, fade = false}: {rows: SkeletonRow[]; fade?: boolean}) => (
  <SkeletonFrame>
    {rows.map((row, index) => (
      <Box key={index} style={fade ? {opacity: 1 - index * 0.2} : undefined}>
        <SkeletonCardRow row={row} divider={index > 0} />
      </Box>
    ))}
  </SkeletonFrame>
);

// A lone open card: its tinted header (title, then More details), then on the
// title column its Progress list (the north star's box, the fold under it),
// fading since what's in it isn't known yet.
const SkeletonOpenCard = () => (
  <SkeletonFrame open>
    <SkeletonCardRow row={SKELETON_ROWS[2]} open />
    <Box
      paddingY="16"
      style={{
        paddingLeft: `calc(${CARD_INSET_PX + STATUS_COLUMN_PX + COLUMN_GAP_PX}px + var(--dsys-breakout-inset, 0px))`,
        paddingRight: CARD_INSET_PX,
      }}
    >
      <Stack gap="16">
        <Box style={{maxWidth: UPDATE_MAX_WIDTH_PX, opacity: 0.6}}>
          <Skeleton.Box height={72} borderRadius="Medium" style={{maxWidth: 'none'}} />
        </Box>
        <Box style={{opacity: 0.4}}>
          <Skeleton.Text size="small" width={144} />
        </Box>
      </Stack>
    </Box>
  </SkeletonFrame>
);

// SectionLabel's box, so the cards below land where the loaded ones will.
const SkeletonSectionLabel = ({width}: {width: SkeletonWidth}) => (
  <Box display="flex" alignItems="center" style={{minHeight: 24, marginBottom: 12}}>
    <Skeleton.Text size="small" width={width} />
  </Box>
);

// How many cards a run shows; the count isn't known yet, so they fade out.
const FADED_RUN = SKELETON_ROWS.slice(0, 4);

const ScanListSkeleton = ({shape}: {shape: ScanSkeletonShape}) => (
  <Box aria-busy="true" aria-label="Loading workstreams">
    {shape === 'card' ? (
      <SkeletonOpenCard />
    ) : shape === 'list' ? (
      <SkeletonCards rows={FADED_RUN} fade />
    ) : (
      <Stack gap="24">
        {/* A parent's own card, collapsed like the cards under it. */}
        {shape === 'parent' && <SkeletonCards rows={SKELETON_ROWS.slice(4)} />}
        <Box>
          <SkeletonSectionLabel width={shape === 'parent' ? 112 : 96} />
          <SkeletonCards rows={FADED_RUN} fade />
        </Box>
      </Stack>
    )}
  </Box>
);

/**
 * /workstreams (default list view): the Execution view's cards
 * (`?table=1` swaps in WorkstreamListTable). One card per workstream: a fixed status column
 * (status + freshness) beside the title, DRI / sub-workstream / milestone line,
 * and a 2-line update excerpt; clicking a card expands its full update,
 * metrics | milestones, and sub-workstreams in place. Rows keep the order they
 * are passed in (the same server sort as /workstreams).
 */
export const WorkstreamScanList = ({
  workstreams,
  loading,
  skeleton = 'list',
  onLeaveUpdate,
  sections,
  loneAccessory,
  parent,
  hideEmptyText,
  place,
}: {
  workstreams: ScanWorkstream[];
  loading: boolean;
  // The page listing these, sent with its cards' events.
  place?: WorkstreamsViewPlace;
  // What shows while loading: the shape the page is about to take.
  skeleton?: ScanSkeletonShape;
  // With nothing to list, show nothing (the page already says why).
  hideEmptyText?: boolean;
  onLeaveUpdate: (id: number) => void;
  // A parent's own card, above the sections of its sub-workstreams' cards
  // (they're listed there, so its card leaves them out).
  parent?: ScanWorkstream;
  // In a lone, open card's header (unsectioned lists only): the focused
  // workstream's facepile.
  loneAccessory?: ReactNode;
  // Splits the list into stacked runs (My workstreams: the rest, then Enablement
  // under its label). Every section's `archived` pools into one collapsed
  // Archived section below them all. Sections with no active cards are skipped;
  // `workstreams` is ignored.
  sections?: Array<{
    key: string;
    // Text, or a link (e.g. a pillar's header opening the pillar).
    label?: ReactNode;
    // Explains the section, in a Toggletip after its label.
    tooltip?: string;
    // Sits after the label instead of the Toggletip (e.g. a pillar's narrative link).
    action?: ReactNode;
    workstreams: ScanWorkstream[];
    archived?: ScanWorkstream[];
  }>;
}) => {
  const visibleSections = useMemo(
    () => sections?.filter((section) => section.workstreams.length > 0),
    [sections]
  );
  // One fold for every section's archived, most recently archived first.
  const archived = useMemo(() => {
    const byId = new Map(
      (sections ?? []).flatMap((section) => section.archived ?? []).map((ws) => [ws.id, ws])
    );
    return [...byId.values()].sort(
      (a, b) => daysSinceUpdate(a) - daysSinceUpdate(b) || a.name.localeCompare(b.name)
    );
  }, [sections]);
  const fadeInRef = useSkeletonFadeIn(loading);

  return (
    <ScanPlaceOrigin place={place}>
      <Box ref={fadeInRef} paddingTop="24" paddingBottom="16">
        {/* The page gutter and the detail page's cap, so cards and the page that
          opens over them share edges. Open cards grow right, up to wide. */}
        <Layout.MaxWidth width="reading" inset breakout>
          {loading ? (
            <ScanListSkeleton shape={skeleton} />
          ) : visibleSections && (parent || visibleSections.length > 0 || archived.length > 0) ? (
            <Stack gap="24">
              {parent && (
                <ScanSectionOrigin section="parent">
                  <RowGroup
                    // Starts collapsed, like its sub-workstreams' cards below it.
                    workstreams={[parent]}
                    onlyGroup={false}
                    hideSubworkstreams
                    onLeaveUpdate={onLeaveUpdate}
                  />
                </ScanSectionOrigin>
              )}
              {visibleSections.map((section) => (
                <Box key={section.key}>
                  <ScanSectionOrigin section={section.key}>
                    {section.label && (
                      <SectionLabel
                        action={
                          section.action ??
                          (section.tooltip && (
                            <InlineToggletip title={section.tooltip} placement="top" />
                          ))
                        }
                      >
                        {section.label}
                      </SectionLabel>
                    )}
                    <RowGroup
                      workstreams={section.workstreams}
                      // A parent's card above counts as another card in view.
                      onlyGroup={visibleSections.length === 1 && !parent}
                      onLeaveUpdate={onLeaveUpdate}
                    />
                  </ScanSectionOrigin>
                </Box>
              ))}
              {archived.length > 0 && (
                <ScanSectionOrigin section="archived">
                  <ArchivedSection archived={archived} onLeaveUpdate={onLeaveUpdate} />
                </ScanSectionOrigin>
              )}
            </Stack>
          ) : (visibleSections ?? workstreams).length === 0 ? (
            hideEmptyText ? null : (
              <Text size="small" color="subtle">
                No workstreams here.
              </Text>
            )
          ) : (
            <RowGroup
              workstreams={workstreams}
              onLeaveUpdate={onLeaveUpdate}
              loneAccessory={loneAccessory}
            />
          )}
        </Layout.MaxWidth>
      </Box>
    </ScanPlaceOrigin>
  );
};
